// Shared GrowthBook client. Lives outside App.jsx so non-React callers (
// utils/everflow, which runs at boot) can await the same init without an import
// cycle — utils/paywall already imports everflow.
//
// Attributes are set from the URL before init so URL-targeted rules resolve on
// first evaluation. `countryCode` is geo-resolved and applied asynchronously
// after init — see gbCountryReady.

import { GrowthBook } from '@growthbook/growthbook';
import { getVariation } from '../utils/variation';
import { autoAttributesPlugin } from '@growthbook/growthbook/plugins';

const COUNTRY_CODE_KEY = 'csf_gb_country_code';

let resolvedCountryCode = '';

const setGbCountryCode = (countryCode) => {
  if (!countryCode) return;
  resolvedCountryCode = countryCode;
  try {
    sessionStorage.setItem(COUNTRY_CODE_KEY, countryCode);
  } catch (e) {
    // sessionStorage disabled (incognito / blocked) — in-memory value still works
  }
};

export const getGbCountryCode = () => {
  if (resolvedCountryCode) return resolvedCountryCode;
  if (typeof window === 'undefined') return '';
  try {
    resolvedCountryCode = sessionStorage.getItem(COUNTRY_CODE_KEY) || '';
  } catch (e) {
    // ignore
  }
  return resolvedCountryCode;
};

export const getGbGeoAttributes = () => {
  const countryCode = getGbCountryCode();
  return countryCode ? { query: countryCode, countryCode } : {};
};

const geoStickyAutoAttributesPlugin = () => {
  const plugin = autoAttributesPlugin();
  return (gb) => {
    if (typeof gb.updateAttributes === 'function') {
      const update = gb.updateAttributes.bind(gb);
      gb.updateAttributes = (attributes) =>
        update({ ...attributes, ...getGbGeoAttributes() });
    }
    plugin(gb);
  };
};

export const growthbook = new GrowthBook({
  apiHost: process.env.REACT_APP_GB_API,
  clientKey: process.env.REACT_APP_GB_KEY,
  enableDevMode: process.env.NODE_ENV === 'development',
  trackingCallback: (experiment, result) => {
    console.log('Viewed Experiment', {
      experimentId: experiment.key,
      variationId: result.key,
    });
  },
  plugins: [geoStickyAutoAttributesPlugin()],
});

(() => {
  const params = new URLSearchParams(window.location.search);
  const urlAttrs = {};
  for (const [key, value] of params.entries()) urlAttrs[key] = value;
  growthbook.setAttributes({
    ...growthbook.getAttributes(),
    url: window.location.href,
    ...urlAttrs,
    // After urlAttrs so the attribute always carries the RESOLVED arm: `variation`
    // rides in the URL only on the landing hit, and getVariation() falls back to
    // this tab's sticky value on the param-less SPA / OAuth-return loads.
    variation: getVariation(),
    ...getGbGeoAttributes(),
  });
})();

// Resolves once features are loaded. Callers needing a deadline must race this
// themselves — do NOT pass `timeout` to init(): the SDK only subscribes to
// late-arriving feature data when streaming is on, so a timed-out init would
// leave the app with an empty payload for the whole page load.
export const gbReady = growthbook.init({ streaming: false });

export const getGbDeviceId = () => {
  if (typeof document === 'undefined') return '';
  try {
    const match = document.cookie.match(/(?:^|;\s*)gbuuid=([^;]+)/);
    if (match) return decodeURIComponent(match[1]);
    return String(growthbook.getAttributes()?.id || '');
  } catch (e) {
    return '';
  }
};

// Deliberately NOT awaited before init(): that would stall every other flag
// behind a network round-trip. setAttributes re-evaluates and the React SDK
// re-renders consumers, and checkout happens long after page load, so the value
// is settled well before any price is printed. A failed lookup leaves the
// multiplier at its default of 1 — the behaviour before this existed.
export const gbCountryReady = (async () => {
  try {
    const res = await fetch(
      `${process.env.REACT_APP_SWIPEY_BASE_URL}/api/v1/auth/country-code`,
    );
    if (!res.ok) return '';
    const data = await res.json();
    const countryCode = data?.countryCode || '';
    if (countryCode) {
      setGbCountryCode(countryCode);
      growthbook.setAttributes({
        ...growthbook.getAttributes(),
        ...getGbGeoAttributes(),
      });
    }
    return countryCode;
  } catch (e) {
    console.warn('[growthbook] country-code lookup failed:', e);
    return '';
  }
})();

// Keeps a rejected init from surfacing as an unhandled rejection; consumers
// still observe the rejection on the original promise.
gbReady.catch(() => {});
